import "bootstrap";

import { css, html, LitElement } from "lit";
import { customElement, query, state } from "lit/decorators.js";
import { transformExtent } from "ol/proj";

import "./components/ol/ol-map";
import "./components/ol/layers/ol-osm-tilelayer";
import "./app/swp-header";
import "./app/layers/swp-school";
import "./app/layers/swp-dangerzones";
import "./app/layers/swp-yellow-foot";
import "./app/swp-image-viewer";
import { getPanoramaProperties, isFeatureWithProperties } from "./app/layers/feature-types";
import { OlFeatureClickEvent } from "./components/ol/ol-feature-click-event";

type ImageViewerElement = HTMLElement & {
    show(request: {
        imageSrc: string;
        title?: string;
        yaw?: number;
        pitch?: number;
    }): Promise<void>;
};

@customElement("swp-app")
export class SwpApp extends LitElement {
    static styles = [
        css`
            :host {
                display: block;
                height: 100%;
                width: 100%;
            }
        `,
    ];

    @query("swp-image-viewer")
    private _imageViewer!: ImageViewerElement;

    @state()
    private _layerYellowFootsVisible = true;

    @state()
    private _layerDangerzonesVisible = true;

    protected createRenderRoot(): HTMLElement | DocumentFragment {
        return this;
    }

    protected render(): unknown {
        const extent = transformExtent([8.219723, 49.397809, 8.259350, 49.414752], "EPSG:4326", "EPSG:3857");

        return html`
            <swp-header></swp-header>

            <ol-map .extent=${extent} @ol-feature-click=${this._handleFeatureClicked}>
                <ol-osm-tilelayer></ol-osm-tilelayer>
                <swp-school></swp-school>
                <swp-yellow-foot .visible=${this._layerYellowFootsVisible}></swp-yellow-foot>
                <swp-dangerzones .visible=${this._layerDangerzonesVisible}></swp-dangerzones>
            </ol-map>

            <swp-image-viewer></swp-image-viewer>

            <div class="offcanvas offcanvas-end" tabindex="-1" id="offcanvasMapOptions" aria-labelledby="offcanvasExampleLabel">
                <div class="offcanvas-header">
                    <h5 class="offcanvas-title" id="offcanvasExampleLabel">Optionen</h5>
                    <button type="button" class="btn-close text-reset" data-bs-dismiss="offcanvas" aria-label="Schließen"></button>
                </div>
                <div class="offcanvas-body">
                    <div class="card">
                        <div class="card-header">Ebenen</div>
                        <div class="card-body">
                            <p class="card-text">Um die Übersicht zu verbessern, können hier die verschieden Informationsebenen ausgeblendet werden.</p>
                            <div class="form-check form-switch">
                                <input id="checkboxLayerYellowFootsVisible" class="form-check-input" type="checkbox" ?checked=${this._layerYellowFootsVisible} @change=${this._handleLayerYellowFootsVisibleChecked}>
                                <label class="form-check-label" for="checkboxLayerYellowFootsVisible">Gelbe Füße</label>
                            </div>
                            <div class="form-check form-switch">
                                <input id="checkboxLayerDangerzonesVisible" class="form-check-input" type="checkbox" ?checked=${this._layerDangerzonesVisible} @change=${this._handleLayerDangerzonesVisibleChecked}>
                                <label class="form-check-label" for="checkboxLayerDangerzonesVisible">Gefahrenstellen</label>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        `;
    }

    private _handleLayerYellowFootsVisibleChecked(event: Event): void {
        this._layerYellowFootsVisible = (event.target as HTMLInputElement).checked;
    }

    private _handleLayerDangerzonesVisibleChecked(event: Event): void {
        this._layerDangerzonesVisible = (event.target as HTMLInputElement).checked;
    }

    private _handleFeatureClicked(event: OlFeatureClickEvent): void {
        const feature = event.detail.features
            .filter(isFeatureWithProperties)
            .sort((a, b) => Number(b.get("priority-rank") ?? 0) - Number(a.get("priority-rank") ?? 0))
            .map((candidate) => ({ candidate, panorama: getPanoramaProperties(candidate) }))
            .find(({ panorama }) => panorama.imageSrc);

        if (!feature?.panorama.imageSrc) {
            return;
        }

        void this._imageViewer.show({
            imageSrc: new URL(feature.panorama.imageSrc, document.baseURI).toString(),
            title: feature.panorama.label,
            yaw: feature.panorama.imageYaw,
            pitch: feature.panorama.imagePitch,
        });
    }
}
